import { Errors } from '@Common/Helpers/Errors';
import { LayoutBuilder } from '@Common/Controls/LayoutBuilder';
import { IGridLayoutParameters } from '@Common/Controls/GridLayout';
import { IRadioParameters, Radio } from '@Common/Controls/Radio';
import { BaseCompositeControl } from '@Common/Controls/BaseCompositeControl';
import { Label } from '@Common/Controls/Label';
import { enumerable } from '@Common/Core/CoreDecorator';

interface IRadioGroupSetting<TValue> {
    title?: string;
    value?: TValue;
    defaultSetting?: Partial<IRadioParameters>;
    items: (Partial<IRadioParameters> & { caption: string; value: TValue })[];
}

export class RadioGroup<TValue = any> extends BaseCompositeControl {
    private readonly title: Label = new Label();
    private readonly items: Radio[] = [];
    public readonly onValueChanged = this.eventBags.create<ICallback<TValue>>();

    constructor(private readonly setting: IRadioGroupSetting<TValue>) {
        super();
        this.createRoot();
    }

    protected initLayout(): IGridLayoutParameters {
        const builder = new LayoutBuilder();
        let className = '';
        if (!_.isEmpty(this.setting.title)) {
            this.title.text(this.setting.title);
            builder.addOneControl(this.title);
            className = 'gap-2-left';
        }

        for (const initSetting of this.setting.items) {
            const setting = _.assign(
                {
                    getRadioButtonsFromOneGroup: () => this.items,
                    checked: initSetting.value === this.setting.value
                },
                this.setting.defaultSetting,
                initSetting
            );
            const radio = new Radio(setting);
            radio.tag(initSetting.value);
            radio.onChange(() => this.onValueChanged(radio.tag() as TValue));
            this.items.push(radio);
            builder.addOneControl(radio, false, null, [className, 'gap-1-bottom'].join(' '));
        }
        return builder.getLayout();
    }

    @enumerable
    public get value(): TValue {
        const checkedRadio = _.find(this.items, r => r.value() === true);
        if (checkedRadio) {
            return checkedRadio.tag();
        }
        return undefined;
    }

    public set value(value: TValue) {
        const radio = _.find(this.items, r => r.tag() === value);
        if (!radio) {
            Errors.invalidOperation(`value '${value}' not in [${this.items.map(r => r.tag()).join(', ')}]`);
        }
        radio.value(true);
    }
}
